import { useAuthStore } from '@/store/authStore';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const DashboardContent = styled.div`
  padding: 2.5rem 0;
`;

const Header = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const StatContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div<{ $bgColor: string }>`
  flex-shrink: 0;
  background: ${(props) => props.$bgColor};
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const StatDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatName = styled.dt`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-600);
  margin-bottom: 0.25rem;
`;

const StatValueRow = styled.dd`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const StatChange = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--success-600);
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;

  p {
    color: var(--gray-500);
    font-size: 0.875rem;
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ActionCard = styled(Link)`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const ActionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const ActionDescription = styled.p`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Dashboard: FC = () => {
  const { user } = useAuthStore();

  const stats = [
    {
      name: 'Total Orders',
      value: '124',
      change: '+12.5%',
      icon: ShoppingCartIcon,
      color: '#2563eb',
      bgColor: '#dbeafe',
    },
    {
      name: 'Total Customers',
      value: '1,248',
      change: '+8.2%',
      icon: UserGroupIcon,
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    {
      name: 'Revenue',
      value: '$24,567',
      change: '+23.1%',
      icon: CurrencyDollarIcon,
      color: '#9333ea',
      bgColor: '#f3e8ff',
    },
    {
      name: 'Conversion Rate',
      value: '3.65%',
      change: '+4.3%',
      icon: ChartBarIcon,
      color: 'var(--primary-600)',
      bgColor: 'rgba(234, 88, 12, 0.1)',
    },
  ];

  return (
    <DashboardContainer>
      <DashboardContent>
        <Header>
          <Title>Welcome back, {user?.full_name}!</Title>
          <Subtitle>Here's what's happening with your store today</Subtitle>
        </Header>

        <Main>
          {/* Stats Grid */}
          <StatsGrid>
            {stats.map((stat) => (
              <StatCard key={stat.name}>
                <StatContent>
                  <IconWrapper $bgColor={stat.bgColor}>
                    <stat.icon style={{ color: stat.color }} />
                  </IconWrapper>
                  <StatDetails>
                    <dl>
                      <StatName>{stat.name}</StatName>
                      <StatValueRow>
                        <StatValue>{stat.value}</StatValue>
                        <StatChange>{stat.change}</StatChange>
                      </StatValueRow>
                    </dl>
                  </StatDetails>
                </StatContent>
              </StatCard>
            ))}
          </StatsGrid>

          {/* Recent Activity */}
          <Section>
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardBody>
                <EmptyState>
                  <p>
                    No recent activity to display. Start by creating your first
                    product!
                  </p>
                </EmptyState>
              </CardBody>
            </Card>
          </Section>

          {/* Quick Actions */}
          <QuickActionsGrid>
            <ActionCard to="/products">
              <ActionTitle>Manage Products</ActionTitle>
              <ActionDescription>
                Add, edit, or remove products from your catalog
              </ActionDescription>
            </ActionCard>
            <ActionCard to="/orders">
              <ActionTitle>View Orders</ActionTitle>
              <ActionDescription>
                Track and manage your customer orders
              </ActionDescription>
            </ActionCard>
            <ActionCard to="/analytics">
              <ActionTitle>Analytics</ActionTitle>
              <ActionDescription>
                View detailed reports and insights
              </ActionDescription>
            </ActionCard>
          </QuickActionsGrid>
        </Main>
      </DashboardContent>
    </DashboardContainer>
  );
};

export default Dashboard;
